import { createContext } from "react";
import { GridSortModel, GridFilterModel } from "@leap4/frontend";
import { initialReportObj, IReport, IReportingResult, IValidationErrors } from "./report.interface";
import { QueryStatus as QueryStatusType } from "./hooks/useAsyncQuery";
import type { IReportingMetadata } from "./hooks/useReportingMetadata";

export interface IPaginationModel {
    page: number;
    pageSize: number;
    cursor?: string | null;
    nextCursor?: string | null;
    hasMore?: boolean;
}

export type CursorCache = Map<number, string | null>;

export interface IReportContext {
    tabIndex: number;
    reportTemplates: IReport[];
    columns: any[];
    report: IReport;
    showReportSettings: boolean;
    setReport: (report: IReport) => void;
    setTabIndex: (tabIndex: number) => void;
    reportData: IReportingResult[] | null;

    /** The chart's rows: the same report grouped by date alone, unpaginated. Null until it lands, and
     *  null for a report with no date dimension, which has nothing to chart its own metrics against. */
    chartRows: IReportingResult[] | null;
    runReport: () => void;
    isFetchingReportData: boolean;

    /** The backend's own reason for the last failure, so the grid can tell "nothing
     *  matched" apart from "the request was rejected". */
    reportError: string | null;

    metadata: IReportingMetadata;
    validationErrors: IValidationErrors;
    totalCount: number;

    totals: IReportingResult | null;
    paginationModel: IPaginationModel;
    onPaginationModelChange: (model: IPaginationModel) => void;

    sortModel: GridSortModel;
    onSortModelChange: (model: GridSortModel) => void;

    filterModel: GridFilterModel;
    onFilterModelChange: (model: GridFilterModel) => void;
    exportAllAsCsv: () => void;
    exportLeadIds: () => void;
    isExporting: boolean;
    isExportingLeadIds: boolean;
    queryStatus: QueryStatusType | null;
    queryElapsedMs: number;
    cancelQuery: () => void;
}

export const ReportContext = createContext<IReportContext>({
    tabIndex: 0,
    reportTemplates: [],
    columns: [],
    report: initialReportObj,
    showReportSettings: false,
    setReport: (_report: IReport) => { },
    setTabIndex: (_tabIndex: number) => { },
    reportData: null,
    chartRows: null,
    runReport: () => { },
    isFetchingReportData: false,
    reportError: null,
    metadata: {
        dimensions: [],
        metrics: [],
        grains: { preaggregated: [], servedRollup: [] },
        labels: new Map(),
        loaded: false,
    },
    validationErrors: {} as IValidationErrors,
    totalCount: 0,
    totals: null,
    paginationModel: { page: 0, pageSize: 100 },
    onPaginationModelChange: (_model: IPaginationModel) => { },
    sortModel: [],
    onSortModelChange: (_model: GridSortModel) => { },
    filterModel: { items: [] },
    onFilterModelChange: (_model: GridFilterModel) => { },
    exportAllAsCsv: () => { },
    exportLeadIds: () => { },
    isExporting: false,
    isExportingLeadIds: false,
    queryStatus: null,
    queryElapsedMs: 0,
    cancelQuery: () => { },
});
